import i18n from "@/i18n";
import {
  CalDavConfigurationForm,
  ExternalProvider,
  FeaturePayload,
  FeatureType,
  GoogleCalendarItem,
  ProviderAccount,
  ProviderAccountFeature,
  ProviderAccountItem,
  ProviderFeature,
} from "@/models/providerAccount";
import { RoomDeserializer, RoomDetail } from "@/models/room";
import axios from "axios";
import apiService from "./apiService";

/**
 * `$t` with an English fallback: the runtime catalogue is the published Tolgee export, so a
 * key added here is missing there until it is published (and `$t` would print the key).
 */
export const translateOr = (key: string, fallback: string): string =>
  i18n.te(key) ? (i18n.t(key) as string) : fallback;

export const supportedAccounts = [
  {
    provider: "MicrosoftExchange",
    slug: "microsoftExchange",
    name: "Microsoft Exchange",
    logo: require("@/assets/images/providers/microsoft.png"),
    calendarlogo: require("@/assets/images/providers/calendars/microsoft.png"),
    videoconferencelogo: require("@/assets/images/providers/videoconference/microsoft.png"),
    description: i18n.t("providers.descriptions.MicrosoftExchange"),
    requirementsLink: "",
    features: ["Calendar"],
  },
  {
    provider: "Microsoft",
    slug: "microsoft",
    name: "Microsoft",
    logo: require("@/assets/images/providers/microsoft.png"),
    calendarlogo: require("@/assets/images/providers/calendars/microsoft.png"),
    videoconferencelogo: require("@/assets/images/providers/videoconference/microsoft.png"),
    description: i18n.t("providers.descriptions.Microsoft"),
    requirementsLink:
      "https://kb.mago.io/requirements/calendar/microsoft-365-calendar",
    features: ["Calendar", "JoinMeeting", "AuthCall", "HostMeeting", "Chat"],
  },
  {
    provider: "Google",
    slug: "google",
    name: "Google",
    logo: require("@/assets/images/providers/google.png"),
    calendarlogo: require("@/assets/images/providers/calendars/google.png"),
    videoconferencelogo: require("@/assets/images/providers/videoconference/google.png"),
    description: i18n.t("providers.descriptions.Google"),
    requirementsLink:
      "https://kb.mago.io/requirements/calendar/google-workspace-calendar",
    features: ["Calendar", "JoinMeeting", "AuthCall", "HostMeeting"],
  },
  {
    provider: "Zoom",
    slug: "zoom",
    name: "Zoom",
    logo: require("@/assets/images/providers/zoom.png"),
    calendarlogo: require("@/assets/images/providers/calendars/zoom.png"),
    videoconferencelogo: require("@/assets/images/providers/videoconference/zoom.png"),
    description: i18n.t("providers.descriptions.Zoom"),
    requirementsLink:
      "https://kb.mago.io/requirements/third-party-providers/zoom",
    features: ["JoinMeeting", "HostMeeting"],
  },
  {
    provider: "Webex",
    slug: "webex",
    name: "Cisco Webex",
    logo: require("@/assets/images/providers/cisco_webex.png"),
    calendarlogo: require("@/assets/images/providers/calendars/cisco_webex.png"),
    videoconferencelogo: require("@/assets/images/providers/videoconference/cisco_webex.png"),
    description: i18n.t("providers.descriptions.Webex"),
    requirementsLink:
      "https://kb.mago.io/requirements/third-party-providers/webex",
    features: ["JoinMeeting", "HostMeeting"],
  },
  {
    provider: "Mago",
    slug: "mago",
    name: "Mago Meet",
    logo: require("@/assets/images/providers/mago_meet.png"),
    calendarlogo: require("@/assets/images/providers/calendars/mago_meet.png"),
    videoconferencelogo: require("@/assets/images/providers/videoconference/mago_meet.png"),
    description: i18n.t("providers.descriptions.MagoMeet"),
    features: ["JoinMeeting", "HostMeeting"],
  },
  {
    provider: "OpenVisio",
    slug: "openVisio",
    name: "OpenVisio",
    logo: require("@/assets/images/providers/openvisio.png"),
    calendarlogo: require("@/assets/images/providers/calendars/openvisio.png"),
    videoconferencelogo: require("@/assets/images/providers/videoconference/openvisio.png"),
    description: i18n.t("providers.descriptions.OpenVisio"),
    features: ["JoinMeeting", "HostMeeting"],
    requiresCapability: true,
  },
  {
    // Live Collaboration (Orange Business: BlueMind calendar + Pexip meetings). The slug is
    // the capability key the Room declares — the gate is `slug in capabilities`,
    // case-sensitive.
    provider: "LiveCollaboration",
    slug: "liveCollaboration",
    name: "Live Collaboration",
    logo: require("@/assets/images/providers/live_collaboration.png"),
    calendarlogo: require("@/assets/images/providers/calendars/live_collaboration.png"),
    videoconferencelogo: require("@/assets/images/providers/videoconference/live_collaboration.png"),
    description: translateOr(
      "providers.descriptions.LiveCollaboration",
      "You can enable joining Live Collaboration meetings using a meeting ID and PIN: no account is required. Connecting a Live Collaboration account (username and password) enables calendar access on the display."
    ),
    // Like the other providers: joining by alias + PIN needs no account; the calendar appears
    // once an account is connected (username + password instead of OAuth — the room reads the
    // calendar over CalDAV with it). No instant meetings — no create API yet.
    features: ["Calendar", "JoinMeeting"],
    requiresCapability: true,
  },
] as ProviderAccountItem[];

/**
 * The Live Collaboration calendar server (BlueMind), read by the room over CalDAV. It is ours
 * to set per environment — the admin only enters the account — and goes to the room with it.
 */
export const LIVE_COLLABORATION_CALENDAR_URL = (
  process.env.VUE_APP_LIVE_COLLABORATION_CALENDAR_URL || ""
).trim();

/**
 * Who is connected on Live Collaboration: the external account the backend creates on Connect.
 * Its e-mail is the user name; the configuration, password included, stays on the backend.
 */
export const liveCollaborationAccountName = (
  account?: ProviderAccount
): string | undefined => account?.email || undefined;

/**
 * The configuration saved when a Live Collaboration account is connected: our server plus the
 * account. No OAuth: the room reads the calendar itself with it.
 */
export const liveCollaborationAccountConfig = (
  form: CalDavConfigurationForm
): Record<string, string> => ({
  serverUrl: LIVE_COLLABORATION_CALENDAR_URL,
  username: form.username,
  password: form.password,
});

export const getProviderBySlug = (
  slug: string
): ProviderAccountItem | undefined =>
  supportedAccounts.find((a) => a.slug === slug);

export type WizardExternalConfig = {
  calendarProviders: ExternalProvider[];
  meetingProviders: ExternalProvider[];
  hasCalendar: boolean;
  hasMeeting: boolean;
};

/**
 * Derives the wizard calendar/meeting steps and their provider lists from a device's
 * clientConfig.wizardSetting, supporting both the version-1 format
 * ({version, externalAccount}) and the legacy {calendar, videoCall} shape. Shared by
 * the /activate and /activate-tab wizards so every entry point parses both formats.
 */
export const getWizardExternalConfig = (
  /* eslint-disable-next-line  @typescript-eslint/explicit-module-boundary-types */
  wizardSetting: any
): WizardExternalConfig => {
  if (!wizardSetting) {
    return {
      calendarProviders: [],
      meetingProviders: [],
      hasCalendar: false,
      hasMeeting: false,
    };
  }

  if (wizardSetting.version != null && wizardSetting.externalAccount) {
    // New format (version 1+): parse externalAccount
    const calendarProviders: ExternalProvider[] = [];
    const meetingProviders: ExternalProvider[] = [];
    for (const [key, value] of Object.entries(wizardSetting.externalAccount)) {
      const account = getProviderBySlug(key);
      if (!account) continue;
      const provider = account.provider;
      const config = value as Record<string, unknown>;
      if (config.calendar) calendarProviders.push(provider);
      if (config.meeting) meetingProviders.push(provider);
    }
    return {
      calendarProviders,
      meetingProviders,
      hasCalendar: calendarProviders.length > 0,
      hasMeeting: meetingProviders.length > 0,
    };
  }

  // Legacy format: calendar/videoCall on wizardSetting
  const calendarProviders: ExternalProvider[] = wizardSetting.calendar
    ? (["Microsoft", "Google"] as ExternalProvider[])
    : [];
  const meetingProviders: ExternalProvider[] = wizardSetting.videoCall
    ? (supportedAccounts
        // Legacy devices never declared capabilities: only offer the historical
        // provider set, capability-gated providers wouldn't work on those builds.
        .filter(
          (a) => a.features.includes("JoinMeeting") && !a.requiresCapability
        )
        .map((a) => a.provider) as ExternalProvider[])
    : [];
  return {
    calendarProviders,
    meetingProviders,
    hasCalendar: calendarProviders.length > 0,
    hasMeeting: meetingProviders.length > 0,
  };
};

export const getSlugByProvider = (
  provider: ExternalProvider
): string | undefined =>
  supportedAccounts.find((a) => a.provider === provider)?.slug;

export const findAccountByProvider = (
  provider: string,
  room: RoomDetail
): ProviderAccount | false => {
  const accounts = room.externalAccounts?.accounts || [];
  const account = accounts.find(
    (acc: ProviderAccount) => acc.provider === provider
  );
  return account ?? false;
};

export const getProvidersByfeature = (
  accounts: ProviderAccount[],
  feature: FeatureType,
  addNone: boolean
): ProviderAccountFeature[] => {
  const providers = supportedAccounts.filter(
    (account) =>
      feature !== "Calendar" ||
      account.provider === "Google" ||
      account.provider === "Microsoft"
  );
  const featuresList: ProviderAccountFeature[] = providers.map(
    (baseAccount: ProviderAccountItem) => {
      const account = accounts.find(
        (acc: ProviderAccount) => acc.provider === baseAccount.provider
      );
      return {
        name: baseAccount.name,
        provider: baseAccount.provider,
        logo:
          feature == "Calendar" ? baseAccount.calendarlogo : baseAccount.logo,
        feature: feature,
        enabled:
          account?.features.some(
            (f: ProviderFeature) => f.featureType === feature
          ) || false,
        account,
        description: baseAccount.description,
      };
    }
  );
  if (addNone) {
    return [
      {
        name: "None",
        logo: "",
        enabled: !featuresList.some((calendar) => calendar.enabled),
        account: undefined,
        feature: feature,
        description: "",
        provider: undefined,
      } as ProviderAccountFeature,
      ...featuresList,
    ];
  } else {
    return featuresList;
  }
};

export const getProviderCalendars = async (
  externalAccountId: string
): Promise<GoogleCalendarItem[]> => {
  const res = await axios.get(
    apiService.integrations.providers.googleCalendars(),
    {
      params: { externalAccountId },
    }
  );
  return res.data?.calendars ?? [];
};

export const fetchGoogleCalendars = async (
  room: RoomDetail
): Promise<[GoogleCalendarItem[], string | null]> => {
  let calendars = [] as GoogleCalendarItem[];
  let selectedCalendarId = null;
  const googleAccount = findAccountByProvider("Google", room);
  if (googleAccount) {
    calendars = await getProviderCalendars(googleAccount.podExternalAccountId);
    const configuredCalendarId =
      googleAccount.configuration?.calendar.calendarId;
    const configuredCalendar = configuredCalendarId
      ? calendars.find((calendar) => calendar.id === configuredCalendarId)
      : null;
    const primaryCalendar = calendars.find((calendar) => calendar.primary);
    selectedCalendarId = configuredCalendar
      ? configuredCalendar.id
      : primaryCalendar
      ? primaryCalendar.id
      : null;
  }
  return [calendars, selectedCalendarId];
};

export const toggleFeature = async (
  feature: FeaturePayload,
  podId: string
): Promise<RoomDetail> => {
  const res = await axios.put(
    apiService.integrations.providers.toggleFeature(podId),
    feature
  );
  return RoomDeserializer(res.data);
};

export const getProviderActivationFeature = (
  provider: ExternalProvider
): FeatureType => {
  if (provider === "Zoom" || provider === "Webex") {
    return "AuthCall";
  }

  return "Default";
};

export const wizardDisableProvider = async (
  podExternalAccountId: string,
  deviceConfigurationId: string,
  sessionId: string
): Promise<void> => {
  await axios.delete(
    `${apiService.integrations.providers.wizardSessionExternalAccounts(
      deviceConfigurationId,
      sessionId
    )}/${podExternalAccountId}`
  );
};
export const wizardToggleFeature = async (
  feature: FeaturePayload,
  deviceId: string,
  deviceConfigurationId: string,
  sessionId: string,
  clientRedirectUrl?: string
): Promise<RoomDetail> => {
  const res = await axios.put(
    apiService.integrations.providers.wizardSessionExternalAccounts(
      deviceConfigurationId,
      sessionId
    ),
    // apiService.integrations.providers.wizardstoggleFeature(),
    {
      ...feature,
      deviceId: deviceId,
      deviceConfigurationId: deviceConfigurationId,
      clientRedirectUrl: clientRedirectUrl,
    }
  );
  return RoomDeserializer(res.data);
};

export const getWizardExternalAccounts = async (
  deviceConfigId: string,
  sessionId: string
): Promise<unknown> => {
  const res = await axios.get(
    // apiService.integrations.providers.wizardExternalAccounts(deviceConfigId)
    apiService.integrations.providers.wizardSessionExternalAccounts(
      deviceConfigId,
      sessionId
    )
  );
  return res.data;
};

const POPUP_CHECK_INTERVAL = 500;
const ZOOM_LOGOUT_DELAY = 300; // Time to wait for logout to complete before redirecting to login

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types */
export const openLoginPopup = (url: string, callback: () => Promise<any>) => {
  const popup = window.open(url, "_blank", "width=600,height=700");
  const watcher = window.setInterval(() => {
    if (popup?.closed) {
      window.clearInterval(watcher);
      callback();
    }
  }, POPUP_CHECK_INTERVAL);
};

/**
 * Opens a single popup for Zoom OAuth that first logs out, then redirects to login.
 * This avoids popup blocker issues on Windows/Safari where a second popup would be blocked.
 */
/* eslint-disable-next-line  @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types */
export const openZoomLoginWithLogout = (
  loginUrl: string,
  callback: () => Promise<any>
) => {
  const popup = window.open(
    "https://zoom.us/logout",
    "oauth_popup",
    "width=600,height=700"
  );

  if (!popup) {
    // Popup blocked, try direct login
    openLoginPopup(loginUrl, callback);
    return;
  }

  // Wait for logout to complete, then redirect to login
  // Note: cross-origin restrictions prevent detecting page load, so we use a fixed delay
  setTimeout(() => {
    if (popup && !popup.closed) {
      popup.location.href = loginUrl;
    }
  }, ZOOM_LOGOUT_DELAY);

  // Monitor for when user closes the popup to run callback
  const checkClosed = setInterval(() => {
    if (popup.closed) {
      clearInterval(checkClosed);
      callback();
    }
  }, POPUP_CHECK_INTERVAL);
};

export const isSafari = (): boolean => {
  const ua = navigator.userAgent;
  return /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
};
